// Office.js helpers for the COMPOSE surface (templates picker + tracked send).
// Mirrors office.ts (which handles the READ surface) but uses the compose-only
// async setters. Everything is client-side in the taskpane; ReadWriteItem in the
// manifest is what permits writing the draft.

export type ComposeRecipient = { email: string; name: string };

// Detect which surface we're on. In compose, item.to is an Office.Recipients
// object (async getter); in read it's a plain EmailAddressDetails[]. This is a
// reliable discriminator across Outlook hosts.
export const isComposeMode = (): boolean => {
  const item = Office.context?.mailbox?.item as unknown as
    | { to?: { getAsync?: unknown } }
    | undefined;
  return typeof item?.to?.getAsync === 'function';
};

const composeItem = (): Office.MessageCompose => {
  const item = Office.context?.mailbox?.item as Office.MessageCompose | undefined;
  if (!item) throw new Error('No compose window is open.');
  return item;
};

const promisify = <T>(
  run: (cb: (result: Office.AsyncResult<T>) => void) => void,
): Promise<T> =>
  new Promise((resolve, reject) => {
    run((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result.value);
      else reject(result.error);
    });
  });

const readRecipients = (
  field: Office.Recipients,
): Promise<ComposeRecipient[]> =>
  promisify<Office.EmailAddressDetails[]>((cb) => field.getAsync(cb)).then((list) =>
    list
      .map((r) => ({ email: r.emailAddress ?? '', name: r.displayName ?? '' }))
      .filter((r) => r.email),
  );

export type ComposeDraft = {
  subject: string;
  to: ComposeRecipient[];
  cc: ComposeRecipient[];
  fromEmail: string;
  fromName: string;
};

// Read the draft's current recipients + subject. The sender is the signed-in
// mailbox owner (the add-in is gated to the CLS mailbox elsewhere).
export const readComposeDraft = async (): Promise<ComposeDraft> => {
  const item = composeItem();
  const [subject, to, cc] = await Promise.all([
    promisify<string>((cb) => item.subject.getAsync(cb)),
    readRecipients(item.to),
    readRecipients(item.cc),
  ]);
  const profile = Office.context.mailbox.userProfile;
  return {
    subject: subject ?? '',
    to,
    cc,
    fromEmail: profile.emailAddress ?? '',
    fromName: profile.displayName ?? '',
  };
};

export const getBodyHtml = (): Promise<string> =>
  promisify<string>((cb) =>
    composeItem().body.getAsync(Office.CoercionType.Html, cb),
  );

export const setBodyHtml = (html: string): Promise<void> =>
  promisify<void>((cb) =>
    composeItem().body.setAsync(html, { coercionType: Office.CoercionType.Html }, cb),
  );

// Insert template HTML at the cursor rather than replacing the body, so the
// user's signature (auto-inserted by Outlook) is preserved.
export const insertBodyHtml = (html: string): Promise<void> =>
  promisify<void>((cb) =>
    composeItem().body.setSelectedDataAsync(
      html,
      { coercionType: Office.CoercionType.Html },
      cb,
    ),
  );

// Only set the subject when it's currently empty, so inserting a template never
// clobbers a subject the user already typed.
export const setSubjectIfEmpty = async (subject: string): Promise<void> => {
  if (!subject) return;
  const item = composeItem();
  const current = await promisify<string>((cb) => item.subject.getAsync(cb));
  if (current.trim()) return;
  await promisify<void>((cb) => item.subject.setAsync(subject, cb));
};

// Per-draft custom properties persist on the message item itself, so a second
// click of "Add tracking" on the same draft updates the existing tracking record
// instead of creating a duplicate loggedEmail.
const TOKEN_PROP = 'clsCrmTrackingToken';
const RECORD_PROP = 'clsCrmLoggedEmailId';

const loadProps = (): Promise<Office.CustomProperties> =>
  promisify<Office.CustomProperties>((cb) => composeItem().loadCustomPropertiesAsync(cb));

export const getTrackingState = async (): Promise<{
  token: string | null;
  loggedEmailId: string | null;
}> => {
  const props = await loadProps();
  return {
    token: (props.get(TOKEN_PROP) as string) ?? null,
    loggedEmailId: (props.get(RECORD_PROP) as string) ?? null,
  };
};

export const saveTrackingState = async (
  token: string,
  loggedEmailId: string,
): Promise<void> => {
  const props = await loadProps();
  props.set(TOKEN_PROP, token);
  props.set(RECORD_PROP, loggedEmailId);
  await promisify<void>((cb) => props.saveAsync(cb));
};
